import React, { useState, useEffect } from 'react';
import { auth, db } from '../../firebase';
import { collection, doc, onSnapshot, updateDoc, query, setDoc, runTransaction, serverTimestamp, getDoc } from 'firebase/firestore';
import { FileText, DollarSign, Clock, AlertCircle, Upload, Plus, MessageSquare, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

export default function CRMManager() {
  const [users, setUsers] = useState<any[]>([]);
  const [currentUserRole, setCurrentUserRole] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [guidelines, setGuidelines] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', role: 'Cliente', plan: 'Aura Connect', maxScreens: '1', slug: '', fiscalName: '', nif: '' });

  useEffect(() => {
    const fetchRole = async () => {
      const useruid = auth.currentUser?.uid;
      if (useruid) {
        const docSnap = await getDoc(doc(db, 'users', useruid));
        if (docSnap.exists()) {
          setCurrentUserRole(docSnap.data().role || '');
        }
      }
    };
    fetchRole();

    // Escuchar a todos los usuarios para la lista del CRM
    const q = query(collection(db, 'users'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(usersData);
      
      // Update selected user info if it changes
      if (selectedUser) {
        const updated = usersData.find(u => u.id === selectedUser.id);
        if (updated) setSelectedUser(updated);
      }
    });

    return unsubscribe;
  }, [selectedUser?.id]);

  const displayedUsers = users.filter(u => {
    if (['admin', 'Admin'].includes(currentUserRole) || ['holasolonet@gmail.com', 'cinside.info@gmail.com'].includes(auth.currentUser?.email || '')) return true;
    if (['sales', 'Comercial'].includes(currentUserRole)) {
      return u.createdBy === auth.currentUser?.uid;
    }
    return false;
  });

  // Sincronizar directrices cuando cambia el usuario seleccionado
  useEffect(() => {
    if (selectedUser) {
      setGuidelines(selectedUser.crmGuidelines || '');
    }
  }, [selectedUser?.id]);

  const handleCreateUser = async () => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No user logged in');
      const idToken = await user.getIdToken();

      // Redirect to run.app for the API call if custom domain proxy blocks POST
      const baseUrl = (window.location.hostname.includes('run.app') || window.location.hostname.includes('localhost')) 
          ? '' 
          : 'https://prod-aura-display-3-136354148420.europe-west1.run.app';

      const formData = new URLSearchParams();
      formData.append('email', newUser.email);
      formData.append('password', newUser.password);
      formData.append('token', idToken);

      const response = await fetch(`${baseUrl}/api/admin/create-client`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData.toString()
      });
      
      const textData = await response.text();
      let resData;
      try {
        resData = JSON.parse(textData);
      } catch (e) {
        throw new Error(`Server returned non-JSON. Status: ${response.status}`);
      }
      
      if (!response.ok) throw new Error(resData?.error || 'Error al crear usuario en auth');

      const newUid = resData.uid;
      const normalizedRole = newUser.role === 'Admin' ? 'admin' : newUser.role === 'Comercial' ? 'sales' : 'client';
      const normalizedPlan = normalizedRole === 'client' ? newUser.plan : 'Interno';

      // Counter via transaction using Client SDK
      let nextId = 101;
      try {
        await runTransaction(db, async (transaction) => {
          const counterRef = doc(db, 'system', 'counters');
          const counterDoc = await transaction.get(counterRef);
          if (counterDoc.exists()) {
             nextId = (counterDoc.data()?.lastClientId || 100) + 1;
          }
          transaction.set(counterRef, { lastClientId: nextId }, { merge: true });
        });
      } catch (err) {
        console.error('Error with transaction:', err);
        nextId = Math.floor(Math.random() * 900) + 100;
      }

      await setDoc(doc(db, 'users', newUid), {
        email: newUser.email,
        role: normalizedRole,
        plan: normalizedPlan,
        maxScreens: normalizedRole === 'client' ? parseInt(newUser.maxScreens) : 0,
        slug: newUser.slug,
        fiscalName: newUser.fiscalName,
        nif: newUser.nif,
        clientId: nextId,
        createdBy: user.uid,
        createdAt: serverTimestamp(),
      });

      await setDoc(doc(db, 'clientes', newUid), {
        isZenMode: false,
        isControlsVisible: true,
        performanceMode: 'normal',
        centralTextScale: 1.0,
        remote_volume: 0,
        showTicker: true,
        clientId: nextId,
        createdAt: serverTimestamp(),
      });

      await setDoc(doc(db, 'displays', newUid), {
        contents: [],
        quotes: [],
        tickers: [],
        createdAt: serverTimestamp()
      }, { merge: true });
      
      alert('¡Cliente creado y asociado a tu cuenta con éxito!');
      setNewUser({ email: '', password: '', role: 'Cliente', plan: 'Aura Connect', maxScreens: '1', slug: '', fiscalName: '', nif: '' });
      setShowCreateModal(false);
    } catch (err) {
      console.error(err);
      alert('Error al crear el usuario. Por favor, reintenta.');
    }
  };

  const saveGuidelines = async () => {
    if (!selectedUser) return;
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'users', selectedUser.id), {
        crmGuidelines: guidelines
      });
      // Animación rápida de guardado
      setTimeout(() => setIsSaving(false), 500);
    } catch (error) {
      console.error("Error al guardar directrices:", error);
      setIsSaving(false);
    }
  };

  const handleManualAddCredits = async () => {
    if (!selectedUser) return;
    await updateDoc(doc(db, 'users', selectedUser.id), {
       auraCredits: (selectedUser.auraCredits || 0) + 100
    });
  };

  if (!selectedUser) {
    return (
      <div className="grid grid-cols-1 gap-4 relative">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-zinc-500 font-bold uppercase text-[10px] tracking-widest">Cartera de Clientes</h2>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500 text-black rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-yellow-400 transition-colors"
          >
            <Plus size={12} /> Nuevo Cliente
          </button>
        </div>

        {/* Create Client Modal */}
        <AnimatePresence>
          {showCreateModal && (
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
              <motion.div 
                initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                className="bg-zinc-950 border border-zinc-800 rounded-xl w-full max-w-md overflow-hidden flex flex-col"
              >
                <div className="p-4 border-b border-zinc-800 flex justify-between items-center">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-300">Dar de Alta Cliente</h3>
                  <button onClick={() => setShowCreateModal(false)} className="text-zinc-500 hover:text-white p-1">
                    <X size={16} />
                  </button>
                </div>
                
                <div className="p-6 space-y-3 overflow-y-auto max-h-[70vh] custom-scrollbar">
                   <p className="text-[10px] text-zinc-400 uppercase tracking-widest mb-4">El cliente quedará asociado automáticamente a tu cartera comercial.</p>
                   
                   <input type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} placeholder="Email de acceso del cliente..." className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-xs focus:border-yellow-500 outline-none" />
                   <input type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} placeholder="Contraseña temporal..." className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-xs focus:border-yellow-500 outline-none" />
                   
                   <div className="grid grid-cols-2 gap-2">
                      <select value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})} className="bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-xs text-zinc-400 outline-none">
                          <option value="Cliente">Rol: Cliente</option>
                          <option value="Admin">Rol: Admin</option>
                          <option value="Comercial">Rol: Comercial</option>
                      </select>
                      {newUser.role === 'Cliente' ? (
                        <select value={newUser.plan} onChange={e => setNewUser({...newUser, plan: e.target.value})} className="bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-xs text-zinc-400 outline-none">
                            <option value="Aura Connect">Plan: Aura Connect</option>
                            <option value="Aura Total">Plan: Aura Total</option>
                        </select>
                      ) : (
                        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-xs text-zinc-600 flex items-center">
                          Uso Interno
                        </div>
                      )}
                   </div>
                   {newUser.role === 'Cliente' && (
                     <input type="number" value={newUser.maxScreens} onChange={e => setNewUser({...newUser, maxScreens: e.target.value})} placeholder="Límite de pantallas (Puntos de emisión)..." className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-xs outline-none" />
                   )}
                   <input type="text" value={newUser.slug} onChange={e => setNewUser({...newUser, slug: e.target.value})} placeholder="URL Amigable (Ej. clinica-dentis)..." className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-xs outline-none" />
                   <input type="text" value={newUser.fiscalName} onChange={e => setNewUser({...newUser, fiscalName: e.target.value})} placeholder="Nombre Comercial o Fiscal..." className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-xs outline-none" />
                   <input type="text" value={newUser.nif} onChange={e => setNewUser({...newUser, nif: e.target.value})} placeholder="NIF / CIF para facturación..." className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-xs outline-none" />
                </div>

                <div className="p-4 border-t border-zinc-800 bg-zinc-900/50">
                  <button onClick={handleCreateUser} className="w-full py-3 rounded-lg bg-yellow-500 text-black font-bold text-[10px] uppercase tracking-widest hover:bg-yellow-400 flex items-center justify-center gap-2">
                     Registrar y Asociar a mi Cartera
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        {displayedUsers.length === 0 ? (
          <div className="p-8 text-center border border-zinc-800 rounded-xl bg-zinc-950/50">
             <p className="text-zinc-600 text-xs">No hay clientes en el CRM.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayedUsers.map(u => (
              <div 
                key={u.id} 
                onClick={() => setSelectedUser(u)}
                className="bg-zinc-950 p-4 border border-zinc-800 rounded-xl cursor-pointer hover:border-zinc-700 transition-colors group"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-bold text-sm text-zinc-200 group-hover:text-white">{u.fiscalName || u.slug || 'Sin Negocio'}</h3>
                    <p className="text-xs text-zinc-500">{u.email}</p>
                    {u.createdBy && <p className="text-[10px] text-zinc-600 mt-1 uppercase tracking-widest">Creado por: {users.find(x => x.id === u.createdBy)?.email || u.createdBy}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-2 text-right">
                    <button 
                      onClick={(e) => { e.stopPropagation(); window.open(`/admin?uid=${u.id}`, '_blank'); }}
                      className="text-[10px] font-bold text-zinc-400 hover:text-yellow-500 transition-colors uppercase"
                    >
                      Aura Connect &rarr;
                    </button>
                    <div className="bg-zinc-900 px-2 py-1 rounded text-[10px] font-bold text-zinc-400 border border-zinc-800">
                      {u.plan || 'Sin Plan'}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 border-t border-zinc-800/50 pt-3 mt-3">
                  <div className="flex flex-col">
                    <span className="text-[9px] uppercase tracking-widest text-zinc-600">Aura Credits</span>
                    <span className={`text-xs font-mono font-bold ${u.auraCredits < 20 ? 'text-red-400' : 'text-zinc-300'}`}>
                      {u.auraCredits || 0}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[9px] uppercase tracking-widest text-zinc-600">Antigüedad</span>
                    <span className="text-xs font-mono font-bold text-zinc-300">
                      {u.createdAt ? 'Varios meses' : 'Nuevo'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Cálculos simulados para Métricas (en producción vendrían de backend/Stripe)
  const ltv = (selectedUser.auraCredits || 0) * 0.15 + 490; // ejemplo $490 base
  const cac = 120; 
  const roas = (ltv / cac).toFixed(1);

  return (
    <div className="flex flex-col gap-6">
      {/* Header Seleccionado */}
      <div className="flex items-center gap-4 pb-4 border-b border-zinc-800">
        <button 
          onClick={() => setSelectedUser(null)}
          className="p-2 bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white rounded-lg"
        >
          &larr; Volver
        </button>
        <div>
          <h2 className="text-lg font-bold text-white leading-none mb-1">{selectedUser.fiscalName || selectedUser.slug || 'Sin Negocio'}</h2>
          <p className="text-xs text-zinc-500">{selectedUser.email}</p>
        </div>
        
        <button 
          onClick={() => window.open(`/admin?uid=${selectedUser.id}`, '_blank')}
          className="ml-auto inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black rounded-lg text-xs font-bold uppercase tracking-widest hover:bg-yellow-400"
        >
          Ir al Panel (Admin)
        </button>

        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 rounded-lg text-xs font-bold uppercase tracking-widest">
           {selectedUser.plan || 'Aura Connect'}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Columa Principal: Directrices y Notas */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare size={16} className="text-blue-400" />
              <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-400">Directrices Musicales/Publicitarias</h3>
            </div>
            <p className="text-[11px] text-zinc-500 mb-4 leading-relaxed">
              Anota aquí reglas específicas para el equipo de contenido. Ej: "No poner música con voces", "Campaña navideña termina el 7 de enero". 
              El equipo verá esto antes de configurar su Soundscape.
            </p>
            <div className="relative">
              <textarea
                value={guidelines}
                onChange={(e) => setGuidelines(e.target.value)}
                placeholder="Escribe las directrices aquí..."
                className="w-full h-32 bg-zinc-900 border border-zinc-800 rounded-lg p-3 text-xs text-zinc-300 resize-none focus:outline-none focus:border-zinc-700"
              />
              <button 
                onClick={saveGuidelines}
                disabled={isSaving || guidelines === selectedUser.crmGuidelines}
                className="absolute bottom-3 right-3 px-4 py-1.5 bg-zinc-800 text-white rounded-md text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isSaving ? 'Guardando...' : 'Guardar Notas'}
              </button>
            </div>
          </div>

          <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
               <FileText size={16} className="text-green-400" />
               <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-400">Historial de Facturación</h3>
            </div>
            
            <div className="border border-dashed border-zinc-800 rounded-xl p-8 flex flex-col items-center justify-center text-center bg-zinc-950/50 hover:bg-zinc-900/50 transition-colors cursor-pointer mb-6">
              <div className="w-10 h-10 bg-zinc-900 rounded-full flex items-center justify-center mb-3">
                <Upload size={16} className="text-zinc-500" />
              </div>
              <p className="text-xs text-zinc-400 font-medium mb-1">Subir factura en PDF</p>
              <p className="text-[10px] text-zinc-600">O arrastra el archivo aquí</p>
            </div>

            <div className="space-y-2">
               {/* Simulación de facturas */}
               <div className="flex items-center justify-between p-3 bg-zinc-900/50 border border-zinc-800/50 rounded-lg">
                 <div className="flex items-center gap-3">
                   <FileText size={14} className="text-zinc-500" />
                   <div>
                     <p className="text-xs font-medium text-zinc-300">Factura_FV2026-004.pdf</p>
                     <p className="text-[10px] text-zinc-500">1 Mayo 2026</p>
                   </div>
                 </div>
                 <span className="text-[10px] font-bold px-2 py-1 bg-green-500/10 text-green-400 rounded uppercase tracking-widest">Pagado</span>
               </div>
               <div className="flex items-center justify-between p-3 bg-zinc-900/50 border border-zinc-800/50 rounded-lg">
                 <div className="flex items-center gap-3">
                   <FileText size={14} className="text-zinc-500" />
                   <div>
                     <p className="text-xs font-medium text-zinc-300">Factura_FV2026-003.pdf</p>
                     <p className="text-[10px] text-zinc-500">1 Abril 2026</p>
                   </div>
                 </div>
                 <span className="text-[10px] font-bold px-2 py-1 bg-green-500/10 text-green-400 rounded uppercase tracking-widest">Pagado</span>
               </div>
            </div>
          </div>
        </div>

        {/* Columna Derecha: Créditos y KPIs */}
        <div className="space-y-6">
          <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-5 relative overflow-hidden">
             
             {/* Indicador de alerta si hay pocos créditos */}
             {selectedUser.auraCredits < 50 && (
                <div className="absolute top-0 right-0 left-0 bg-red-500/20 text-red-400 text-[10px] font-bold text-center py-1 uppercase tracking-widest">
                  Low Balance
                </div>
             )}

             <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-4 mt-2">Control Aura Credits</h3>
             
             <div className="flex flex-col items-center justify-center p-6 bg-zinc-900 rounded-xl mb-4 border border-zinc-800">
               <span className="text-4xl font-black font-mono tracking-tighter text-white mb-2">{selectedUser.auraCredits || 0}</span>
               <span className="text-[10px] uppercase tracking-widest text-zinc-500">Créditos Disponibles</span>
             </div>

             <div className="space-y-3">
               <button onClick={handleManualAddCredits} className="w-full py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-[10px] font-bold uppercase tracking-widest rounded-lg flex items-center justify-center gap-2">
                 <Plus size={14} /> Añadir Bono (100)
               </button>

               {selectedUser.auraCredits < 20 && (
                 <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex gap-3 text-red-400">
                   <AlertCircle size={16} className="shrink-0 mt-0.5" />
                   <p className="text-[10px] leading-relaxed">
                     El cliente está a punto de agotar sus créditos. <strong>Sugerencia del CRM:</strong> Contactar para ofrecer un Bono de Impulsos o upselling a plan superior.
                   </p>
                 </div>
               )}
             </div>
          </div>

          <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-5">
             <div className="flex items-center gap-2 mb-6">
               <DollarSign size={16} className="text-yellow-500" />
               <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-400">Rentabilidad (KPIs)</h3>
             </div>

             <div className="space-y-4">
               <div>
                 <div className="flex justify-between items-end mb-1">
                   <span className="text-[10px] uppercase tracking-widest text-zinc-500">LTV (Lifetime Value)</span>
                   <span className="text-sm font-bold text-zinc-300">${ltv.toFixed(0)}</span>
                 </div>
                 <div className="w-full h-1 bg-zinc-900 rounded-full overflow-hidden">
                    <div className="h-full bg-yellow-500" style={{ width: '70%' }} />
                 </div>
               </div>

               <div>
                 <div className="flex justify-between items-end mb-1">
                   <span className="text-[10px] uppercase tracking-widest text-zinc-500">CAC Estimado</span>
                   <span className="text-sm font-bold text-zinc-300">${cac}</span>
                 </div>
                 <div className="w-full h-1 bg-zinc-900 rounded-full overflow-hidden">
                    <div className="h-full bg-red-500" style={{ width: '40%' }} />
                 </div>
               </div>

               <div className="grid grid-cols-2 gap-3 pt-3 border-t border-zinc-800">
                  <div className="bg-zinc-900 p-3 rounded-lg text-center">
                    <span className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">ROAS</span>
                    <span className="block font-bold text-green-400">{roas}x</span>
                  </div>
                  <div className="bg-zinc-900 p-3 rounded-lg text-center">
                    <span className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Tiempo</span>
                    <span className="block font-bold text-zinc-300">8 Meses</span>
                  </div>
               </div>
             </div>
          </div>
        </div>

      </div>
    </div>
  );
}
